/**
 * Swatch page cart (localStorage) and favorites, including login-modal launch
 * when the user is not authenticated.
 */

import { dispatchSiteEvent, siteEventNames } from '../events/site-events';
import { getItemsByType } from './consumer-account/account-api';

const CART_STORAGE_KEY = 'graberSwatchCart';
const CART_UPDATED_EVENT = 'graber:cart-updated';

const createSwatchCartFavorites = (ctx) => {
  const {
    app,
    detailPanel,
  } = ctx;

  // Held as swatch ids rather than as the clicked elements. Signing in ends in a full page
  // reload, and once the grid is built from the catalog data island instead of server-rendered,
  // anything that re-renders it while the login modal is open would leave a stored element
  // detached from the document - the login would succeed and the add would silently do nothing.
  // An id can always be resolved again; an element reference cannot.
  let pendingFavoriteId = null;
  let pendingFavoriteFromDetail = false;
  let pendingCartId = null;
  let favoritesCount = 0;

  const favoritesCountEl = app.querySelector('#swatch-favorites-count');
  if (favoritesCountEl) {
    favoritesCount = Math.max(0, parseInt(favoritesCountEl.textContent, 10) || 0);
  }

  const animateConfirmation = (button, selected) => {
    if (!button) {
      return;
    }
    const className = selected ? 'is-confirming-select' : 'is-confirming-deselect';
    button.classList.add(className);
    window.setTimeout(() => {
      button.classList.remove(className);
    }, 300);
  };

  const settleCartButton = (button) => {
    if (!button || button.classList.contains('is-settling') || !button.matches(':hover')) {
      return;
    }
    button.classList.add('is-settling');
    button.addEventListener('pointerleave', () => {
      button.classList.remove('is-settling');
    }, { once: true });
  };

  const readCart = () => {
    try {
      const raw = window.localStorage.getItem(CART_STORAGE_KEY);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw);
      return (parsed && Array.isArray(parsed.items)) ? parsed.items : [];
    } catch (e) {
      return [];
    }
  };

  const writeCart = (items) => {
    try {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({ items }));
    } catch (e) {
      // Local storage may be unavailable; fail silently.
    }
    window.dispatchEvent(new window.Event(CART_UPDATED_EVENT));
  };

  // productLineCode is what lets My Cart move a swatch into account favorites, and
  // productLineName completes the cart's description line.
  const buildCartItemFromButton = button => ({
    id: button.getAttribute('data-swatch-id'),
    imageUrl: button.getAttribute('data-image-url'),
    name: button.getAttribute('data-name'),
    colorNumber: button.getAttribute('data-color-number'),
    collection: button.getAttribute('data-collection'),
    colorName: button.getAttribute('data-color-name'),
    swatchCategoryName: button.getAttribute('data-swatch-category-name'),
    productLineCode: button.getAttribute('data-product-line-code'),
    productLineName: button.getAttribute('data-product-line-name'),
  });

  const buildCartItemFromCard = (card) => {
    const btn = card.querySelector('.swatch-cart-btn');
    if (btn) {
      return buildCartItemFromButton(btn);
    }
    return {
      id: card.getAttribute('data-swatch-id'),
      imageUrl: card.getAttribute('data-image-url'),
      name: card.getAttribute('data-color-name'),
      colorNumber: card.getAttribute('data-color-number'),
      collection: card.getAttribute('data-collection'),
      colorName: card.getAttribute('data-color-name'),
      swatchCategoryName: card.getAttribute('data-category'),
      productLineCode: card.getAttribute('data-product-line-code'),
      productLineName: card.getAttribute('data-productline-name'),
    };
  };

  // Goes through the site event registry rather than a bare CustomEvent: the GA4 listener
  // in data-layer-api reads the payload back out with getEventData, so a detail-only event
  // would arrive empty even under the right name.
  const dispatchAddSwatchEvent = (item) => {
    try {
      dispatchSiteEvent(siteEventNames.ADD_SWATCH, item);
    } catch (e) {
      // The registry is created by app.js initSiteEvents; ignore if it has not run.
    }
  };

  const setActionCount = (el, count) => {
    if (!el) {
      return;
    }
    const n = Math.max(0, Number(count) || 0);
    el.textContent = String(n);
    if (n > 0) {
      el.removeAttribute('hidden');
      el.setAttribute('aria-hidden', 'false');
    } else {
      el.setAttribute('hidden', '');
      el.setAttribute('aria-hidden', 'true');
    }
  };

  const updateActionCounts = () => {
    const cartCount = window.isAuthenticated ? readCart().length : 0;
    setActionCount(app.querySelector('#swatch-cart-count'), cartCount);
    setActionCount(favoritesCountEl, favoritesCount);
  };

  const refreshFavoritesCountFromAccount = () => {
    if (!window.isAuthenticated || !favoritesCountEl) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      getItemsByType('swatch', (data) => {
        const swatches = (data && Array.isArray(data.swatches)) ? data.swatches : [];
        favoritesCount = swatches.length;
        updateActionCounts();
        resolve();
      });
    });
  };

  const setCartButtonState = (button, inCart) => {
    if (!button) {
      return;
    }
    button.setAttribute('aria-pressed', inCart ? 'true' : 'false');
    button.classList.toggle('is-in-cart', inCart);

    const label = button.querySelector('.swatch-cart-label');
    if (label) {
      const name = button.getAttribute('data-color-name') || '';
      label.textContent = (inCart ? 'Remove Swatch ' : 'Add Swatch ') + name + (inCart ? ' From Cart' : ' To Cart');
    }
  };

  const syncCartButtonsForId = (swatchId, inCart) => {
    Array.prototype.slice.call(app.querySelectorAll('.swatch-cart-btn')).forEach((button) => {
      if (String(button.getAttribute('data-swatch-id')) === String(swatchId)) {
        setCartButtonState(button, inCart);
      }
    });
    if (typeof ctx.updateDetailAddCartLabel === 'function') {
      ctx.updateDetailAddCartLabel();
    }
    updateActionCounts();
  };

  const addToCartFromButton = (button) => {
    const items = readCart();
    const id = button.getAttribute('data-swatch-id');
    const exists = items.some(item => String(item.id) === String(id));
    if (!exists) {
      const item = buildCartItemFromButton(button);
      items.push(item);
      writeCart(items);
      dispatchAddSwatchEvent(item);
    }
    syncCartButtonsForId(id, true);
    if (!exists) {
      animateConfirmation(button, true);
    }
  };

  const removeFromCart = (id, button) => {
    const items = readCart().filter(item => String(item.id) !== String(id));
    writeCart(items);
    syncCartButtonsForId(id, false);
    animateConfirmation(button, false);
  };

  const toggleCartButton = (button) => {
    const willAdd = button.getAttribute('aria-pressed') !== 'true';
    settleCartButton(button);
    if (willAdd) {
      addToCartFromButton(button);
    } else {
      removeFromCart(button.getAttribute('data-swatch-id'), button);
    }
  };

  const initCartState = () => {
    if (!window.isAuthenticated) {
      updateActionCounts();
      return;
    }

    const inCartIds = {};
    readCart().forEach((item) => {
      inCartIds[String(item.id)] = true;
    });

    Array.prototype.slice.call(app.querySelectorAll('.swatch-cart-btn')).forEach((button) => {
      const id = String(button.getAttribute('data-swatch-id'));
      setCartButtonState(button, !!inCartIds[id]);
    });

    if (typeof ctx.updateDetailAddCartLabel === 'function') {
      ctx.updateDetailAddCartLabel();
    }
    updateActionCounts();
  };

  const postFavoriteForm = (form) => {
    const slot = form.closest('.swatch-favorite-slot')
      || (detailPanel && detailPanel.querySelector('.swatch-detail-favorite-slot'));
    const isAdd = form.getAttribute('data-favorite-action') === 'add';
    const buttons = form.querySelectorAll('button');
    Array.prototype.forEach.call(buttons, (b) => { b.disabled = true; });

    return fetch(form.getAttribute('action'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: new URLSearchParams(new FormData(form)),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Favorite request failed with status ${response.status}`);
      }
      return response.text();
    }).then((html) => {
      const swatchId = form.getAttribute('data-swatch-id');
      Array.prototype.slice.call(app.querySelectorAll('.swatch-favorite-slot')).forEach((s) => {
        const existing = s.querySelector('.swatch-favorite-form');
        if (existing && String(existing.getAttribute('data-swatch-id')) === String(swatchId)) {
          s.innerHTML = html;
        }
      });
      if (detailPanel) {
        const detailSlot = detailPanel.querySelector('.swatch-detail-favorite-slot');
        if (detailSlot) {
          const detailForm = detailSlot.querySelector('.swatch-favorite-form');
          if (detailForm && String(detailForm.getAttribute('data-swatch-id')) === String(swatchId)) {
            detailSlot.innerHTML = html;
          } else if (slot === detailSlot) {
            detailSlot.innerHTML = html;
          }
        }
      }

      // Keep hover-preview from flipping until the pointer leaves the new button.
      const selector = `.swatch-favorite-form[data-swatch-id="${swatchId}"]`;
      const forms = Array.prototype.slice.call(app.querySelectorAll(selector));
      if (detailPanel) {
        const detailForm = detailPanel.querySelector(selector);
        if (detailForm) {
          forms.push(detailForm);
        }
      }
      forms.forEach((nextForm) => {
        nextForm.classList.add('is-settling');
        nextForm.addEventListener('pointerleave', () => {
          nextForm.classList.remove('is-settling');
        }, { once: true });
      });
      if (slot) {
        animateConfirmation(slot.querySelector('.swatch-favorite-btn'), isAdd);
      }

      // The swatch need not have a card in `app` at all: in browse mode the detail panel can be
      // showing a card built from a catalog record, and then the panel's own form is the only
      // copy of what the server returned. Without this fallback the result reads as a failure,
      // which skips the favorites count refresh.
      const sample = app.querySelector(selector)
        || (detailPanel ? detailPanel.querySelector(selector) : null);
      const nowFavorite = !!(sample && sample.getAttribute('data-favorite-action') === 'remove');
      const actionSucceeded = (isAdd && nowFavorite) || (!isAdd && !nowFavorite);

      if (typeof ctx.onFavoriteFormPosted === 'function') {
        ctx.onFavoriteFormPosted({
          form, swatchId, isAdd, nowFavorite, actionSucceeded,
        });
      }

      if (actionSucceeded) {
        return refreshFavoritesCountFromAccount();
      }
      return Promise.resolve();
    }).catch(() => {
      Array.prototype.forEach.call(buttons, (b) => { b.disabled = false; });
    });
  };

  // The reload below throws away the scroll offset and would close the detail panel. The
  // panel is restored from the `detail` URL param that swatch-detail.js maintains; the
  // offset is stashed here for swatch-page.js to pick up on the way back in.
  const reloadPreservingView = () => {
    ctx.saveScrollForReload();
    window.location.reload();
  };

  /**
   * Re-finds the form a pending favorite was started from. The detail panel lives outside `app`,
   * and one swatch can carry a form in both a browse strip and the filtered grid, so the panel
   * is preferred when that is where the click came from.
   */
  const findFavoriteForm = (swatchId, fromDetail) => {
    const selector = `.swatch-favorite-form[data-swatch-id="${swatchId}"]`;

    if (fromDetail && detailPanel) {
      const inDetail = detailPanel.querySelector(selector);
      if (inDetail) {
        return inDetail;
      }
    }

    return app.querySelector(selector);
  };

  const submitPendingFavorite = () => {
    const form = pendingFavoriteId
      ? findFavoriteForm(pendingFavoriteId, pendingFavoriteFromDetail)
      : null;

    pendingFavoriteId = null;
    pendingFavoriteFromDetail = false;

    if (!form) {
      reloadPreservingView();
      return;
    }

    postFavoriteForm(form).then(() => {
      reloadPreservingView();
    });
  };

  const submitPendingCart = () => {
    const button = pendingCartId
      ? app.querySelector(`.swatch-cart-btn[data-swatch-id="${pendingCartId}"]`)
      : null;

    pendingCartId = null;

    if (button) {
      addToCartFromButton(button);
    }

    // Full reload so header cart badge, toolbar counts, and cart button states match account.
    reloadPreservingView();
  };

  const toggleModalCreateForm = (modal) => {
    const loginContainer = modal.querySelector('.account-login-container');
    const loginSide = modal.querySelector('.login-side-column');
    const createContainer = modal.querySelector('.create-account-container');
    const createSide = modal.querySelector('.create-account-side-column');
    const messageWrapper = modal.querySelector('.login-message-wrapper');

    if (loginContainer) {
      loginContainer.classList.add('hidden');
    }
    if (loginSide) {
      loginSide.classList.add('hidden');
    }
    if (createContainer) {
      createContainer.classList.remove('hidden');
    }
    if (createSide) {
      createSide.classList.remove('hidden');
    }
    if (messageWrapper) {
      messageWrapper.classList.add('hidden');
    }
    modal.style.height = '600px';
  };

  /**
   * Bootstrap Offcanvas keeps a document-level focus trap while open. The account
   * login UI is a separate custom modal outside the offcanvas, so the trap steals
   * focus from its inputs. Pause the trap (and inert the panel) until login closes.
   */
  const pauseDetailOffcanvasForAuth = () => {
    if (!detailPanel || !detailPanel.classList.contains('show')) {
      return null;
    }

    let offcanvas = null;
    if (window.bootstrap && window.bootstrap.Offcanvas) {
      offcanvas = window.bootstrap.Offcanvas.getInstance(detailPanel);
      // Bootstrap Offcanvas private focus trap API
      // eslint-disable-next-line no-underscore-dangle
      const focustrap = offcanvas && offcanvas._focustrap;
      if (focustrap) {
        focustrap.deactivate();
      }
    }

    detailPanel.setAttribute('inert', '');
    return offcanvas;
  };

  const resumeDetailOffcanvasAfterAuth = (offcanvas) => {
    if (detailPanel) {
      detailPanel.removeAttribute('inert');
    }

    // Bootstrap Offcanvas private focus trap / shown state
    // eslint-disable-next-line no-underscore-dangle
    const focustrap = offcanvas && offcanvas._isShown && offcanvas._focustrap;
    if (focustrap) {
      focustrap.activate();
    }
  };

  const watchLoginModalClosed = (onClosed) => {
    const modal = document.getElementById('acct-login-modal');
    if (!modal) {
      onClosed();
      return;
    }

    if (!modal.classList.contains('modal-visible')) {
      // launchLoginModal is sync for open; if still not visible, resume shortly.
      window.setTimeout(() => {
        if (!modal.classList.contains('modal-visible')) {
          onClosed();
        }
      }, 0);
    }

    const observer = new window.MutationObserver(() => {
      if (!modal.classList.contains('modal-visible')) {
        observer.disconnect();
        onClosed();
      }
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
  };

  const launchAuthModal = (onSuccess) => {
    if (!window.Login || typeof window.Login.launchLoginModal !== 'function') {
      return;
    }

    const pausedOffcanvas = pauseDetailOffcanvasForAuth();
    let resumed = false;
    const resumeFocus = () => {
      if (resumed) {
        return;
      }
      resumed = true;
      resumeDetailOffcanvasAfterAuth(pausedOffcanvas);
    };

    const callbacks = {
      onLoginSuccess: () => {
        window.isAuthenticated = true;
        resumeFocus();
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
      },
      onLoginFail: () => {
        pendingFavoriteId = null;
        pendingFavoriteFromDetail = false;
        pendingCartId = null;
        resumeFocus();
      },
    };

    window.onSubmitCallbacks = callbacks;
    window.Login.launchLoginModal({ type: '', callbacks });
    watchLoginModalClosed(resumeFocus);

    const modal = document.getElementById('acct-login-modal');
    if (modal) {
      const username = modal.querySelector('#modalAcctUsername, [name="modalAcctUsername"]');
      if (username) {
        window.setTimeout(() => {
          username.focus();
        }, 0);
      }

      const createLink = modal.querySelector('.create-account-btn');
      if (createLink && !createLink.getAttribute('data-swatch-wired')) {
        createLink.setAttribute('data-swatch-wired', 'true');
        createLink.addEventListener('click', (event) => {
          event.preventDefault();
          window.onSubmitCallbacks = callbacks;
          toggleModalCreateForm(modal);
          const createUsername = modal.querySelector('[name="acctUsername"]');
          if (createUsername) {
            setTimeout(() => { createUsername.focus(); }, 0);
          }
        });
      }
    }
  };

  const onFavoriteSubmit = (event) => {
    const form = event.target.closest('.swatch-favorite-form');
    if (!form) {
      return;
    }
    event.preventDefault();

    const isAdd = form.getAttribute('data-favorite-action') === 'add';
    if (isAdd && !window.isAuthenticated) {
      pendingFavoriteId = form.getAttribute('data-swatch-id');
      pendingFavoriteFromDetail = !!(detailPanel && detailPanel.contains(form));
      const favoriteBtn = form.querySelector('.swatch-favorite-btn');
      if (favoriteBtn) {
        favoriteBtn.blur();
      }
      launchAuthModal(submitPendingFavorite);
      return;
    }

    postFavoriteForm(form);
  };

  const onCartClick = (button) => {
    if (!window.isAuthenticated) {
      pendingCartId = button.getAttribute('data-swatch-id');
      button.blur();
      launchAuthModal(submitPendingCart);
      return;
    }
    toggleCartButton(button);
  };

  window.addEventListener(CART_UPDATED_EVENT, updateActionCounts);
  window.addEventListener('storage', (event) => {
    if (!event || event.key === null || event.key === CART_STORAGE_KEY) {
      updateActionCounts();
    }
  });

  return {
    readCart,
    writeCart,
    buildCartItemFromCard,
    dispatchAddSwatchEvent,
    removeFromCart,
    syncCartButtonsForId,
    initCartState,
    onCartClick,
    onFavoriteSubmit,
    launchAuthModal,
  };
};

export default createSwatchCartFavorites;
export { createSwatchCartFavorites, CART_STORAGE_KEY, CART_UPDATED_EVENT };
